import React, { useState, useEffect, useRef, useContext } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { blobStorageService } from "common/services/blob-storage.service";

// import { BlobServiceClient } from '@azure/storage-blob';
// const sasToken = "sv=2022-11-02&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2025-09-26T01:35:08Z&st=2024-09-25T17:35:08Z&spr=https,http&sig=rDbopxqocQzV3PVNic%2FYerFaJ2OsL7Inb4BQ3QkXUAQ%3D"
//const sasToken = "sp=r&st=2024-08-27T02:51:25Z&se=2025-09-01T10:51:25Z&spr=https&sv=2022-11-02&sr=c&sig=g3D2exqJs8CFOhG6zB4A7m4KAwcZ%2F2hLbUiqMiJYOBw%3D";
// const blobServiceClient = new BlobServiceClient(`http://localhost:3000/docstore?${sasToken}`);
// const blobServiceClient = new BlobServiceClient(`https://gdeblob1.blob.core.windows.net?${sasToken}`);

interface ShowBlobProps {
  blobName: string;
  onBlobLoaded: (
    blobUrl: string,
    canvasRef: React.RefObject<HTMLCanvasElement>
  ) => void;
  doc_id?: string | number;
  flowId?: string | number;
}

type FileType = "pdf" | "excel&doc" | "image" | null;

const ShowBlob = ({
  blobName,
  onBlobLoaded,
  doc_id,
  flowId
}: ShowBlobProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<FileType>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const style = { width: "100%", height: "100%", overflow: "auto" };

  const sanitizeFileName = (fileName: string): string => {
    // Split the filename into name and extension
    const lastDotIndex = fileName.lastIndexOf(".");
    const name =
      lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension =
      lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";

    // Replace unwanted characters in the name (only allow letters, numbers, hyphen, underscore, and equals)
    const sanitizedName = name.replace(/[^a-zA-Z0-9\-_=]/g, "_");

    // Return the sanitized name with its original extension
    return sanitizedName + extension;
  };

  useEffect(() => {
    const downloadAndDisplayFile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Cancel previous request if exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Check if required parameters are available
        if (!doc_id || !flowId) {
          throw new Error(
            "Both doc_id and flowId are required to view the blob"
          );
        }

        // Fetch blob using the service
        const blobContent = await blobStorageService.fetchBlobForView(
          {
            document_id: Number(doc_id),
            flow_id: Number(flowId)
          },
          abortControllerRef.current.signal
        );

        let blobURL: any;
        if (blobName.toLowerCase().endsWith(".pdf")) {
          // For PDFs, create blob URL with proper type
          blobURL = URL.createObjectURL(
            new Blob([blobContent], { type: "application/pdf" })
          );
          setFileType("pdf");
          setBlobUrl(blobURL);
          // Keep isLoading true for PDFs until iframe loads
        } else if (
          blobName.toLowerCase().endsWith(".docx") ||
          blobName.toLowerCase().endsWith(".xlsx") ||
          blobName.toLowerCase().endsWith(".csv")
        ) {
          blobURL = URL.createObjectURL(blobContent);
          setBlobUrl(blobURL);
          setFileType("excel&doc");
          setIsLoading(false);
        } else {
          blobURL = URL.createObjectURL(blobContent);
          setFileType("image");
          setBlobUrl(blobURL);
          setIsLoading(false);
        }
      } catch (downloadError: any) {
        if (downloadError.name === "AbortError") {
          return;
        }
        console.error("Error downloading blob:", downloadError);
        setError("Failed to load the file. Please try again.");
        setIsLoading(false);
      }
    };

    if (blobName) {
      downloadAndDisplayFile();
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Revoke blob URL to free memory
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobName, doc_id, flowId]);

  useEffect(() => {
    if (canvasRef.current && blobUrl) {
      const image = new Image();
      image.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const aspectRatio = image.naturalHeight / image.naturalWidth;

        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;

        const displayWidth = canvas.clientWidth;
        const displayHeight = displayWidth * aspectRatio;
        canvas.style.height = `${displayHeight}px`;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
          onBlobLoaded(blobUrl, canvasRef);
        }
      };
      image.src = blobUrl;
    }
  }, [blobUrl, fileType, onBlobLoaded]);

  const handleDownloadClick = () => {
    if (!blobUrl) return;

    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", blobName); // Set the download attribute with the file name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px", color: "red" }}>
        {error}
      </div>
    );
  }

  if (fileType === "image") {
    return <canvas ref={canvasRef} style={style} />;
  } else if (fileType === "pdf") {
    // Keep PDF toolbar and controls visible for zoom, page navigation, and save options
    const pdfUrlWithControls = `${blobUrl}#view=FitH`;

    return (
      <div style={{ position: "relative", width: "100%", height: "72vh" }}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "25%",
              transform: "translate(-50%, -50%)",
              zIndex: 10
            }}
          >
            <CircularProgress />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={pdfUrlWithControls}
          title="PDF content"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            display: isLoading ? "none" : "block"
          }}
          onLoad={() => setIsLoading(false)}
          loading="lazy"
        />
      </div>
    );
  } else if (fileType === "excel&doc") {
    return (
      <div>
        Preview is currently not available for excel and word files.....
        <a
          href="#"
          onClick={handleDownloadClick}
          style={{
            textDecoration: "underline",
            color: "blue",
            cursor: "pointer"
          }}
        >
          Click here to download the file.
        </a>
      </div>
    );
  } else if (isLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <CircularProgress />
      </div>
    );
  }

  return null;
};

export default ShowBlob;
